import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Chip,
  Container,
  Divider,
  Button,
  alpha,
  Paper,
  Avatar,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  LinearProgress
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SchoolIcon from '@mui/icons-material/School';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useTheme } from '@mui/material/styles';
import { useParams, useNavigate } from 'react-router-dom';

import languageLearningApi from '../middleware/languageLearningApi';
import Sidebar from './Sidebar';
import { jwtDecode } from "jwt-decode";


// Language background mapping
const languageBackgrounds = {
  en: {
    mainBg: "https://images.unsplash.com/photo-1501594907352-04cda38ebc29",
    flag: "https://flagcdn.com/w320/us.png"
  },
  de: {
    mainBg: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b",
    flag: "https://flagcdn.com/w320/de.png"
  },
  es: {
    mainBg: "https://images.unsplash.com/photo-1515443961218-a51367888e4b",
    flag: "https://flagcdn.com/w320/es.png"
  },
  fr: {
    mainBg: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34",
    flag: "https://flagcdn.com/w320/fr.png"
  },
  ko: {
    mainBg: "https://images.unsplash.com/photo-1538669735225-088dd86bde4e",
    flag: "https://flagcdn.com/w320/kr.png"
  },
  ja: {
    mainBg: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e",
    flag: "https://flagcdn.com/w320/jp.png"
  },
  zh: {
    mainBg: "https://images.unsplash.com/photo-1547981609-4b6bfe67ca0b",
    flag: "https://flagcdn.com/w320/cn.png"
  },
  ar: {
    mainBg: "https://images.unsplash.com/photo-1519682577862-22b62b24e493",
    flag: "https://flagcdn.com/w320/ae.png"
  },
  ru: {
    mainBg: "https://images.unsplash.com/photo-1513326738677-b964603b136d",
    flag: "https://flagcdn.com/w320/ru.png"
  },
  pt: {
    mainBg: "https://images.unsplash.com/photo-1555881400-74d7acaacd8b",
    flag: "https://flagcdn.com/w320/pt.png"
  },
  // Default fallback
  default: {
    mainBg: "https://images.unsplash.com/photo-1589519160732-576f165b9aad",
    flag: "https://flagcdn.com/w320/un.png"
  }
};

const ViewCourseContent = ({token}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [courseForSidebar, setCourseForSidebar] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [viewMode, setViewMode] = useState('card');
  
  // New states for user progress
  const [userProgress, setUserProgress] = useState(null);
  const [activeCourselet, setActiveCourselet] = useState(null);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [backgroundImages, setBackgroundImages] = useState({
    mainBg: "",
    flag: ""
  });

  const { courseId } = useParams();
  const decoded = jwtDecode(token);
  const userId = decoded.user._id; // This should come from auth context or similar

  // Helper function to find a courselet by ID in all sections
  const findCourseletById = (id, sectionsData) => {
    for (const section of sectionsData) {
      if (!section.courselets) continue;
      
      const foundCourselet = section.courselets.find(courselet => courselet._id === id);
      if (foundCourselet) return foundCourselet;
    }
    return null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch aggregated course data (single API call instead of multiple)
        const aggregatedCourseRes = await languageLearningApi.get(`/aggregated/course/${courseId}`);
        const aggregatedCourse = aggregatedCourseRes.data;
        
        // Extract course data
        const courseData = {
          _id: aggregatedCourse.courseId,
          title: aggregatedCourse.title,
          description: aggregatedCourse.description,
          language: aggregatedCourse.language,
          deckId: aggregatedCourse.deckId,
          createdBy: aggregatedCourse.createdBy,
          createdAt: aggregatedCourse.createdAt
        };
        
        setCourse(courseData);
        setCourseForSidebar(courseData);

        // Set background images based on language code
        const languageCode = courseData.language || 'default';
        setBackgroundImages(languageBackgrounds[languageCode] || languageBackgrounds.default);

        // Transform sections with courselets
        const transformedSections = aggregatedCourse.sections.map(section => {
          return {
            _id: section.sectionId,
            title: section.title,
            description: section.description,
            order: section.order,
            createdAt: section.createdAt,
            courselets: section.courselets.map(courselet => ({
              _id: courselet.courseletId,
              title: courselet.title,
              domain: courselet.domain,
              order: courselet.order,
              createdAt: courselet.createdAt
            })).sort((a, b) => a.order - b.order)
          };
        }).sort((a, b) => a.order - b.order);
        
        setSections(transformedSections);

        // Fetch user progress (still needs a separate call as it's user-specific)
        const progressRes = await languageLearningApi.get(`/learning-queue/user-progress?userId=${userId}&courseId=${courseId}`);
        const progressData = progressRes.data.userCourseProgress;
        setUserProgress(progressData);

        // Calculate progress percentage
        const totalCourselets = transformedSections.reduce(
          (total, section) => total + section.courselets.length, 0
        );
        const completedCount = progressData?.completedCourselets?.length || 0;
        setProgressPercentage(Math.round((completedCount / totalCourselets) * 100));

        // Handle active courselet
        if (progressData?.activeCourselets?.length > 0) {
          const activeCourseLetId = progressData.activeCourselets[0];
          
          // Find the active courselet in the aggregated data
          const existingCourselet = findCourseletById(activeCourseLetId, transformedSections);
          
          if (existingCourselet) {
            setActiveCourselet(existingCourselet);
          }
        }

        // Automatically expand the first section only
        const initialExpandedState = {};
        if (transformedSections.length > 0) {
          initialExpandedState[transformedSections[0]._id] = true;
        }
        setExpandedSections(initialExpandedState);

      } catch (err) {
        setError(err.message || 'An error occurred while fetching data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [courseId, userId]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleCourseLetClick = (courseletId) => {
    navigate(`/view-courselet/${courseletId}`);
  };

  const handleContinueLearning = () => {
    navigate(`/course-learn`);
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f8f9fa'
        }}
      >
        <Box sx={{ position: 'relative', mb: 3 }}>
          <CircularProgress
            size={80}
            thickness={4}
            sx={{ color: theme.palette.primary.main }}
          />
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SchoolIcon sx={{ fontSize: 30, color: theme.palette.primary.main }} />
          </Box>
        </Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 1,
            color: theme.palette.primary.main
          }}
        >
          Preparing Your Learning Journey
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}
        >
          Loading materials and sections for your course
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f8f9fa'
        }}
      >
        <Paper
          elevation={4}
          sx={{
            p: 4,
            borderRadius: 2,
            maxWidth: 450,
            textAlign: 'center',
            background: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '5px',
            background: theme.palette.error.main
          }} />
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              fontWeight: 700,
              color: theme.palette.error.main
            }}
          >
            Something went wrong
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            size="large"
            disableElevation
            sx={{
              borderRadius: 2,
              px: 4,
              backgroundColor: theme.palette.error.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.9),
              }
            }}
            onClick={() => navigate('/courses')}
          >
            Back to courses
          </Button>
        </Paper>
      </Box>
    );
  }

  // Calculate the total number of courselets
  const totalCourselets = sections.reduce(
    (total, section) => total + section.courselets.length, 0
  );

  return (
    <Box
      sx={{
        bgcolor: '#f8f9fa',
        minHeight: '100vh',
        py: 4
      }}
    >
      <Container maxWidth="lg">
        {/* Course Header with Background Image */}
        <Paper
          elevation={3}
          sx={{
            mb: 5,
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            // background: `linear-gradient(rgba(255, 255, 255, 0.65), rgba(255, 255, 255, 0.65)), url(${backgroundImages.mainBg})`,
            background: "rgb(242, 242, 242)",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: theme.palette.text.primary,
            position: 'relative',
            boxShadow: '0 6px 0 0 rgba(0, 0, 0, 0.1)'
          }}
        >
          <Typography 
            variant="h2"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              mb: 4,
              color: 'rgb(21, 2, 115)',
              textAlign: 'center',
              textShadow: '1px 1px 1px rgba(0,0,0,0.1)'
            }}
          >
            {course?.title}
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    bgcolor:'white',
                    width: 60,
                    height: 60,
                    border: '2px solid rgb(21, 2, 115)',
                    mr: 2
                  }}
                >
                <SchoolIcon sx={{color:  'rgb(21, 2, 115)', fontWeight: 500}}/>
                </Avatar>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                    color: 'rgb(21, 2, 115)'
                  }}
                >
                  This Course Contains:
                </Typography>
              </Box>

              
              <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={`${sections.length} Sections`}
                  color="primary"
                  variant="outlined"
                  size="medium"
                  sx={{background: 'white', color:  'rgb(21, 2, 115)', fontWeight: 500, border: '2px solid rgb(21, 2, 115)'}}
                  icon={<MenuBookIcon />}
                />

                <Chip
                  label={`${totalCourselets} Lessons`}
                  color="primary"
                  variant="outlined"
                  size="medium"
                  sx={{background: 'white', color:  'rgb(21, 2, 115)', fontWeight: 500, border: '2px solid rgb(21, 2, 115)'}}
                  icon={<AutoStoriesIcon />}
                />

                <Chip
                  label="Level: Intermediate"
                  color="primary"
                  variant="outlined"
                  size="medium"
                  sx={{background: 'white', color:  'rgb(21, 2, 115)', fontWeight: 500, border: '2px solid rgb(21, 2, 115)'}}
                  icon={<EmojiEventsIcon />}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Course Progress */}
        <Paper
          elevation={3}
          sx={{
            mb: 5,
            p: 3,
            borderRadius: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            gap: 3,
            background: 'white',
            position: 'relative',
            boxShadow: '0 4px 0 0 rgba(0, 0, 0, 0.1)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', pt: 0.5 }}>
            <Box sx={{ position: 'relative', mr: 2 }}>
              <CircularProgress
                variant="determinate"
                value={progressPercentage}
                size={60}
                thickness={5}
                sx={{
                  color: theme.palette.primary.main,
                  position: 'relative',
                  zIndex: 1
                }}
              />
              <CircularProgress
                variant="determinate"
                value={100}
                size={60}
                thickness={5}
                sx={{
                  color: alpha(theme.palette.primary.main, 0.2),
                  position: 'absolute',
                  top: 0,
                  left: 0
                }}
              />
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: theme.palette.primary.main
                }}
              >
                {progressPercentage}%
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>Course Progress</Typography>
              <Typography variant="body2" color="text.secondary">
                {userProgress?.completedCourselets?.length || 0} of {totalCourselets} lessons completed
              </Typography>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Divider sx={{ width: '100%', display: { xs: 'block', sm: 'none' } }} />

          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Continue where you left off:
              </Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                {activeCourselet ? activeCourselet.title : sections[0]?.courselets[0]?.title || 'First lesson'}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              sx={{
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                boxShadow: '0 4px 0 0 rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 0 0 rgba(0, 0, 0, 0.2)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)',
                }
              }}
              endIcon={<ArrowForwardIcon />}
              onClick={handleContinueLearning}
            >
              Continue Learning
            </Button>
          </Box>
        </Paper>

        {/* Course Sections with View Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary
            }}
          >
            Course Content
          </Typography>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
            size="small"
            sx={{
              backgroundColor: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              borderRadius: 1,
              '& .MuiToggleButton-root': {
                border: 'none',
                mx: 0.5
              }
            }}
          >
            <ToggleButton value="card" aria-label="card view" sx={{ borderRadius: 1 }}>
              <ViewModuleIcon />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view" sx={{ borderRadius: 1 }}>
              <ViewListIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {sections.map((section, sectionIndex) => (
          <Paper
            key={section._id}
            elevation={2}
            sx={{
              mb: 4,
              borderRadius: 2,
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              background: 'white',
              boxShadow: '0 4px 0 0 rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Section Header */}
            <Box
              sx={{
                p: 2.5,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderBottom: expandedSections[section._id] ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08)
                }
              }}
              onClick={() => toggleSection(section._id)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    width: 48,
                    height: 48,
                    mr: 2,
                    fontWeight: 'bold',
                    boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)'
                  }}
                >
                  {sectionIndex + 1}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {section.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <AutoStoriesIcon fontSize="small" />
                      {section.courselets.length} lessons
                    </Typography>

                    {userProgress && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={progressPercentage}
                          sx={{
                            width: 80,
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: alpha(theme.palette.primary.main, 0.1)
                          }}
                        />
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          {progressPercentage}% complete
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              <IconButton
                size="small"
                sx={{
                  transition: 'transform 0.2s ease',
                  transform: expandedSections[section._id] ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: theme.palette.primary.main
                }}
              >
                <KeyboardArrowDownIcon />
              </IconButton>
            </Box>

            {/* Section Content (Courselets) */}
            {expandedSections[section._id] && (
              <Box sx={{ p: 3, bgcolor: 'white' }}>
                {section.description && (
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 3,
                      color: theme.palette.text.secondary,
                      px: 1,
                      lineHeight: 1.6
                    }}
                  >
                    {section.description}
                  </Typography>
                )}

                {viewMode === 'card' ? (
                  <Grid container spacing={2.5}>
                    {section.courselets.map((courselet, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={4} key={courselet._id}>
                        <Card
                          sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 2,
                            transition: 'all 0.2s ease',
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            overflow: 'visible',
                            position: 'relative',
                            boxShadow: '0 6px 0 0 rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 8px 0 0 rgba(0, 0, 0, 0.1)',
                              cursor: 'pointer'
                            },
                            '&:active': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 0 0 rgba(0, 0, 0, 0.1)'
                            }
                          }}
                          onClick={() => handleCourseLetClick(courselet._id)}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -10,
                              left: 16,
                              backgroundColor: '#6c5ce7',
                              color: '#fff',
                              borderRadius: 1,
                              px: 1.5,
                              py: 0.5,
                              zIndex: 2,
                              fontWeight: 'bold',
                              boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)'
                            }}
                          >
                            <Typography variant="body2" fontWeight={700}>
                              Unit {sectionIndex + 1}.{index + 1}
                            </Typography>
                          </Box>

                          <CardContent 
                            sx={{ 
                              flexGrow: 1, 
                              p: 3, 
                              background: `linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), url(${backgroundImages.flag})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat',
                              height: '100%'
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                mb: 2,
                                height: '100%',
                                flexDirection: 'column'
                              }}
                            >
                              <Box sx={{ flexGrow: 1, width: '100%' }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    color: theme.palette.text.primary,
                                    lineHeight: 1.3,
                                    mb: 3,
                                    minHeight: '3.9rem',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                  }}
                                >
                                  {courselet.title}
                                </Typography>

                                {courselet.description && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 3,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      mb: 2
                                    }}
                                  >
                                    {courselet.description}
                                  </Typography>
                                )}
                              </Box>

                              <Box sx={{ mt: 'auto', pt: 2, display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                                <Button
                                  variant="contained"
                                  disableElevation
                                  size="small"
                                  sx={{
                                    borderRadius: 2,
                                    px: 2,
                                    fontWeight: 600,
                                    boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.2)',
                                    backgroundColor: '#6c5ce7',
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 5px 0 0 rgba(0, 0, 0, 0.2)',
                                      backgroundColor: '#5b4cdb'
                                    },
                                    '&:active': {
                                      transform: 'translateY(0)',
                                      boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)',
                                    }
                                  }}
                                  endIcon={<VisibilityIcon />}
                                >
                                  View
                                </Button>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2 }}>
                    
                    {section.courselets.map((courselet, index) => (
                      <React.Fragment key={courselet._id}>
                        <ListItem
                          alignItems="flex-start"
                          sx={{
                            py: 2,
                            px: 3,
                            borderRadius: 2,
                            mb: 1,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                            boxShadow: '0 4px 0 0 rgba(0, 0, 0, 0.08)',
                            backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)), url(${backgroundImages.flag})`,
                            backgroundSize: 'contain',
                            backgroundPosition: 'right',
                            backgroundRepeat: 'no-repeat',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                              transform: 'translateY(-2px)',
                              boxShadow: '0 6px 0 0 rgba(0, 0, 0, 0.08)',
                            },
                            '&:active': {
                              transform: 'translateY(0)',
                              boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.08)',
                            },
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleCourseLetClick(courselet._id)}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                backgroundColor: '#6c5ce7',
                                color: 'white',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)'
                              }}
                            >
                              {sectionIndex + 1}.{index + 1}
                            </Avatar>
                          </ListItemAvatar>

                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight={600}>
                                {courselet.title}
                              </Typography>
                            }
                            secondary={
                              courselet.description ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    mt: 0.5
                                  }}
                                >
                                  {courselet.description}
                                </Typography>
                              ) : null
                            }
                          />

                          <ListItemSecondaryAction>
                            <Button
                              variant="contained"
                              size="small"
                              endIcon={<VisibilityIcon />}
                              sx={{
                                borderRadius: 2,
                                boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.2)',
                                backgroundColor: '#6c5ce7',
                                '&:hover': {
                                  backgroundColor: '#5b4cdb',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 5px 0 0 rgba(0, 0, 0, 0.2)',
                                },
                                '&:active': {
                                  transform: 'translateY(0)',
                                  boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)',
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCourseLetClick(courselet._id);
                              }}
                            >
                              View
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < section.courselets.length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </Paper>
        ))}
      </Container>
    </Box>
  );
};

export default ViewCourseContent;